import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BarChart3, ArrowLeft, Search, Download, Save, Edit3, Check, X, RotateCcw, ChevronUp, ChevronDown } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { ROUTES } from '@/lib/routes';
import type { CategoryKey } from '@/lib/navigation';
import { supabase } from '@/lib/supabaseClient';
import { ensureAuthReady } from '@/lib/ensureAuthReady';
import { toast } from 'sonner';
import { usePageFocus } from '@/hooks/usePageFocus';
import TestSearchHarness from '@/components/TestSearchHarness';
import { triggerReloadSearch, pickReloadSearch } from '@/utils/reloadSearch';
import { companyService } from '@/services/companyService';
import { decodeHtmlEntities } from '@/lib/utils';

interface SalesOrder {
  id: number;
  order_date: string | null;
  order_time: string | null;
  order_id: string;
  sku_id: string;
  company_sku_key: string | null;
  sale_quantity: number;
  sale_unit_price: number;
  sale_commission_rate: number | null;
  sales_commission_amount: number | null;
  snapshot_cost_unit: number | null;
  snapshot_net_revenue: number | null;
  snapshot_gross_profit: number | null;
  total_amount: number | null;
  shipment_id: string | null;
  sale_sku_brand_en: string | null;
  sale_sku_brand_zh: string | null;
  sale_sub_category_code: string | null;
  // Product info from joined query
  product_name?: string | null;
  brand?: string | null;
}

interface SearchFilters {
  startDate: string;
  endDate: string;
  orderId: string;
  skuId: string;
  shipmentId: string;
  brand: string; // 合併的品牌搜尋欄位
  categoryCode: string; // 改名的分類碼
  productName: string; // 改名的產品名稱搜尋
}

interface SortConfig {
  key: keyof SalesOrder | null;
  direction: 'asc' | 'desc' | null;
}

const ORDER_CATEGORY: CategoryKey = 'order';

/** 將 API 回傳值統一為 number | null，避免字串或 NaN 導致排序錯亂 */
function toNum(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const SalesStatistics: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  const [salesData, setSalesData] = useState<SalesOrder[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [currentCompany, setCurrentCompany] = useState<{ id: string; code?: string; name?: string } | null>(null);

  // 排序狀態
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: null
  });
  
  // 使用本地的 loading 狀態（由 runSearch 管理）
  const [loading, setLoading] = useState(false);
  
  // 初始化公司資訊
  useEffect(() => {
    const initializeCompany = async () => {
      try {
        const company = await companyService.getCurrentCompany();
        if (company) {
          setCurrentCompany({ id: String(company.id), code: company.code, name: company.name });
        }
      } catch (error) {
        console.error('Failed to get company:', error);
        toast.error(language === 'zh' ? '獲取公司資訊失敗' : 'Failed to get company information');
      }
    };

    initializeCompany();
  }, [language]);

  // 暫存待回放搜尋條件，確保公司資訊就緒後仍能執行
  const pendingReloadRef = useRef<ReturnType<typeof pickReloadSearch>>(null);

  // 首次載入時讀取回放條件並保存
  useEffect(() => {
    const pending = pickReloadSearch();
    if (!pending) return;

    pendingReloadRef.current = pending;

    // 立即保存待回放的條件到 UI 狀態，避免遺失
    console.log('🔄 保存待回放搜尋條件到 UI 狀態');
    setFilters(pending.filters);
  }, []);

  // 公司資訊就緒後回放搜尋
  useEffect(() => {
    if (!currentCompany?.id) {
      console.log('🔄 等待公司資訊就緒...');
      return;
    }

    const pending = pendingReloadRef.current;
    if (!pending) return;

    const executePendingSearch = async () => {
      console.log('🔄 檢測到待回放的搜索，開始執行');
      try {
        // 保險：確保 Auth/Session ready 再搜
        await supabase.auth.getSession();
        await runSearch(pending.filters);
        toast.success(language === 'zh' ? '搜索條件已恢復並執行' : 'Search conditions restored and executed');
      } catch (error) {
        console.error('回放搜索失敗:', error);
        toast.error(language === 'zh' ? '搜索恢復失敗，請重新搜尋' : 'Search restore failed, please search again');
      } finally {
        pendingReloadRef.current = null;
      }
    };

    executePendingSearch();
  }, [currentCompany, language]); // 移除 runSearch 依賴避免無限循環

  // 頁面焦點恢復處理器
  const handlePageFocusRecovery = useCallback(async () => {
    console.log('🔄 銷售統計頁面焦點恢復，等待 Auth 就緒');
    await ensureAuthReady();
    console.log('🔄 Auth 就緒，但不自動重新搜尋（使用刷新机制）');
    // 不自動重新搜尋，因為搜索会触发页面刷新
  }, []);

  // 使用共享的頁面焦點恢復 hook
  // 禁用自動頁面焦點恢復，由用戶手動控制
  const { triggerFocusRecovery } = usePageFocus({
    onFocus: handlePageFocusRecovery,
    debounceMs: 120,
    enabled: false
  });

  // 組件卸載時清理狀態
  useEffect(() => {
    return () => {
      // 清理搜索、編輯和排序相關狀態
      setSalesData([]);
      setEditingId(null);
      setLoading(false);
      setSortConfig({ key: null, direction: null });
      setFilters({
        startDate: '',
        endDate: '',
        orderId: '',
        skuId: '',
        shipmentId: '',
        brand: '',
        categoryCode: '',
        productName: ''
      });
      setEditValues({
        sale_commission_rate: null,
        snapshot_cost_unit: null
      });
    };
  }, []);
  const [editValues, setEditValues] = useState<{
    sale_commission_rate: number | null;
    snapshot_cost_unit: number | null;
  }>({
    sale_commission_rate: null,
    snapshot_cost_unit: null
  });
  
  // 獲取昨天的日期
  const getYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  // 數值欄位（API 可能回傳字串，需強制數字比較才能得到 負→0→正 的順序）
  const NUMERIC_SORT_KEYS: (keyof SalesOrder)[] = [
    'sale_quantity',
    'sale_unit_price',
    'sale_commission_rate',
    'sales_commission_amount',
    'snapshot_cost_unit',
    'snapshot_net_revenue',
    'snapshot_gross_profit',
    'total_amount'
  ];

  // 排序資料函數：僅用數值比較，確保升序時 負數 → 0 → 正數，且比較器絕不回傳 NaN
  const sortData = (data: SalesOrder[], config: SortConfig): SalesOrder[] => {
    if (!config.key || !config.direction) return data;

    const isNumericKey = NUMERIC_SORT_KEYS.includes(config.key!);
    const dir = config.direction === 'asc' ? 1 : -1;

    return [...data].sort((a, b) => {
      const aVal = a[config.key!];
      const bVal = b[config.key!];

      if (isNumericKey) {
        const aNum = toNum(aVal);
        const bNum = toNum(bVal);
        if (aNum === null && bNum === null) return a.id - b.id;
        if (aNum === null) return 1;   // null 排最後
        if (bNum === null) return -1;
        const diff = dir * (aNum - bNum);
        return diff !== 0 ? diff : a.id - b.id;
      }

      if (aVal == null && bVal == null) return a.id - b.id;
      if (aVal == null) return config.direction === 'asc' ? -1 : 1;
      if (bVal == null) return config.direction === 'asc' ? 1 : -1;

      if (config.key === 'order_date') {
        const tA = new Date(aVal as string).getTime();
        const tB = new Date(bVal as string).getTime();
        const diff = config.direction === 'asc' ? tA - tB : tB - tA;
        return diff !== 0 ? diff : a.id - b.id;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const cmp = config.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        return cmp !== 0 ? cmp : a.id - b.id;
      }

      return a.id - b.id;
    });
  };

  // 用 useMemo 穩定排序結果，避免大量資料時重算導致閃爍或次序不穩
  const sortedSalesData = React.useMemo(
    () => sortData(salesData, sortConfig),
    [salesData, sortConfig]
  );

  // 處理排序點擊
  const handleSort = (key: keyof SalesOrder) => {
    setSortConfig(prevConfig => {
      if (prevConfig.key === key) {
        // 同一個欄位，切換排序方向
        if (prevConfig.direction === 'asc') {
          return { key, direction: 'desc' };
        } else if (prevConfig.direction === 'desc') {
          return { key: null, direction: null }; // 取消排序
        } else {
          return { key, direction: 'asc' };
        }
      } else {
        // 不同欄位，開始升序排序
        return { key, direction: 'asc' };
      }
    });
  };

  // 🔥 收斂查詢函數：每一條都接 signal
  const qSalesOrders = (signal: AbortSignal, filters: SearchFilters, companyId: number) => {
    console.log('🔥 qSalesOrders: 構建查詢，signal.aborted=', signal.aborted, 'companyId=', companyId);
    
    // 保险检查：如果 signal 已经被中止，立即抛出 AbortError
    if (signal.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    
    let query = supabase
      .from('sales_orders')
      .select(`
        id,
        order_date,
        order_time,
        order_id,
        sku_id,
        company_sku_key,
        sale_quantity,
        sale_unit_price,
        sale_commission_rate,
        sales_commission_amount,
        snapshot_cost_unit,
        snapshot_net_revenue,
        snapshot_gross_profit,
        total_amount,
        shipment_id,
        sale_sku_brand_en,
        sale_sku_brand_zh,
        sale_sub_category_code,
        sale_sku_name_en,
        sale_sku_name_zh
      `)
      .eq('company_id', companyId)
      .limit(10000);

    // 應用搜尋條件
    if (filters.startDate) query = query.gte('order_date', filters.startDate);
    if (filters.endDate) query = query.lte('order_date', filters.endDate);
    if (filters.orderId) query = query.ilike('order_id', `%${filters.orderId}%`);
    if (filters.skuId) {
      query = query.or(`sku_id.ilike.%${filters.skuId}%,company_sku_key.ilike.%${filters.skuId}%`);
    }
    if (filters.shipmentId) query = query.ilike('shipment_id', `%${filters.shipmentId}%`);
    if (filters.brand) {
      query = query.or(`sale_sku_brand_en.ilike.%${filters.brand}%,sale_sku_brand_zh.ilike.%${filters.brand}%`);
    }
    if (filters.categoryCode) query = query.ilike('sale_sub_category_code', `%${filters.categoryCode}%`);
    if (filters.productName) {
      query = query.or(`sale_sku_name_en.ilike.%${filters.productName}%,sale_sku_name_zh.ilike.%${filters.productName}%`);
    }

    // ★ 一定要在鏈上接 signal
    return query
      .order('order_date', { ascending: true })
      .order('order_time', { ascending: true })
      .abortSignal(signal);
  };

  // 單次查詢函數（一定要接 signal）
  const fetchSalesData = async (signal: AbortSignal, filters: SearchFilters, companyId: number) => {
    console.log('[A] FETCH begin', { aborted: signal.aborted, companyId });
    
    // Auth 已經在 runSearch 中等待就緒，這裡直接執行查詢
    console.log('🚀 開始執行 Supabase 查詢，條件數量:', Object.values(filters).filter(v => v).length);
    
    // 🔥 使用收斂的查詢函數，確保 signal 正確傳遞
    const startTime = Date.now();
    const { data, error } = await qSalesOrders(signal, filters, companyId);
    
    const queryTime = Date.now() - startTime;
    console.log(`⚡ Supabase 查詢完成，耗時: ${queryTime}ms，結果數量:`, data?.length || 0);

    if (error) {
      console.error('Supabase 查詢錯誤:', error);
      throw error;
    }

    // 處理產品資訊，並將數值欄位統一為 Number（API 可能回傳字串，導致排序錯亂）
    const processedData = data?.map(item => ({
      ...item,
      id: Number(item.id),
      sale_quantity: toNum(item.sale_quantity) ?? 0,
      sale_unit_price: toNum(item.sale_unit_price) ?? 0,
      sale_commission_rate: toNum(item.sale_commission_rate),
      sales_commission_amount: toNum(item.sales_commission_amount),
      snapshot_cost_unit: toNum(item.snapshot_cost_unit),
      snapshot_net_revenue: toNum(item.snapshot_net_revenue),
      snapshot_gross_profit: toNum(item.snapshot_gross_profit),
      total_amount: toNum(item.total_amount),
      product_name: item.sale_sku_name_zh || item.sale_sku_name_en || `SKU: ${item.sku_id}`,
      brand: item.sale_sku_brand_zh || item.sale_sku_brand_en || '-'
    })) || [];

    console.log('[B] FETCH done rows=', processedData?.length ?? 0);
    return processedData;
  };

  const [filters, setFilters] = useState<SearchFilters>({
    startDate: getYesterday(),
    endDate: getYesterday(),
    orderId: '',
    skuId: '',
    shipmentId: '',
    brand: '',
    categoryCode: '',
    productName: ''
  });

  // 穩定的搜尋執行器
  const ctrlRef = useRef<AbortController | null>(null);
  const reqIdRef = useRef(0);

  const runSearch = useCallback(async (searchFilters: SearchFilters) => {
    const myId = ++reqIdRef.current;
    console.log('🔎 開始搜尋請求 [ID:', myId, ']');

    // 檢查是否有公司資訊
    if (!currentCompany?.id) {
      console.warn('🔴 缺少公司資訊，無法執行搜尋');
      toast.error(language === 'zh' ? '缺少公司資訊，請重新載入頁面' : 'Missing company information, please reload the page');
      return;
    }

    try {
      // A) 先等 Auth（**不要**在这段期间设 timeout/abort）
      console.log('🔐 等待 Auth 就緒 [ID:', myId, ']');
      await ensureAuthReady();
      console.log('🔐 Auth 已就緒，開始建立查詢 [ID:', myId, ']');

      // B) 才建立控制器 + 逾时计时器
      ctrlRef.current?.abort();
      const controller = new AbortController();
      ctrlRef.current = controller;
      const { signal } = controller;

      setLoading(true);
      
      // 🧪 生命週期探針
      console.log('🧪[RUN] id=%d, newCtrl, signal.aborted=%s', myId, signal.aborted);
      
      signal.addEventListener('abort', () => {
        console.warn('🧪[ABORT] id=%d fired', myId);
      });

      const timeoutMs = 30000; // 30 seconds timeout - reasonable for database queries
      const timer = setTimeout(() => {
        console.warn('🧪[TIMEOUT→ABORT] id=%d', myId);
        controller.abort();                // ★ 只會終止「資料查詢」，不影響 Auth
      }, timeoutMs);

      try {
        // C) **所有**查詢都必須掛上同一個 signal
        console.log('🚀 開始執行查詢 [ID:', myId, '], signal.aborted=', signal.aborted);
        const result = await fetchSalesData(signal, searchFilters, Number(currentCompany.id));
        console.log('🧪[DONE] id=%d rows=%d', myId, result?.length ?? -1);

        if (reqIdRef.current !== myId) {
          console.log('🟡 非最新請求，丟棄 [ID:', myId, ']');
          return;
        }

        // D) 落地資料
        setSalesData(result);
        console.log('🟢 搜尋完成 [ID:', myId, '], rows=', result.length);
        
        if (result.length === 0) {
          toast.info(language === 'zh' ? '沒有找到符合條件的記錄。請嘗試調整搜尋條件。' : 'No records found matching the criteria. Please try adjusting your search.');
        } else {
          toast.success(language === 'zh' ? `找到 ${result.length} 筆記錄，將顯示 ${result.length} 行` : `Found ${result.length} records, displaying ${result.length} rows`);
        }
      } catch (e: any) {
        if (e.name !== 'AbortError') {
          console.warn('🔴 搜尋錯誤 [ID:', myId, ']', e?.name, e?.message);
          toast.error(language === 'zh' ? '搜尋過程中發生錯誤' : 'Error occurred during search');
        }
      } finally {
        clearTimeout(timer);
        if (reqIdRef.current === myId) setLoading(false);
        if (ctrlRef.current === controller) ctrlRef.current = null;
      }
      
      console.log('🧪[FIN] id=%d setLoading=false', myId);
    } catch (e: any) {
      console.warn('🧪[ERR] id=%d name=%s msg=%s', myId, e?.name, e?.message);
      if (e.name !== 'AbortError') {
        toast.error(language === 'zh' ? '搜尋過程中發生錯誤' : 'Error occurred during search');
      }
    }
  }, [language, currentCompany]);

  // 搜尋銷售訂單 - 使用專業的刷新機制
  const searchSalesOrders = () => {
    if (!filters.startDate && !filters.endDate && !filters.orderId && !filters.skuId && 
        !filters.shipmentId && !filters.brand && !filters.categoryCode && !filters.productName) {
      toast.error(language === 'zh' ? '請至少填入一個搜尋條件' : 'Please enter at least one search criteria');
      return;
    }

    // 檢查日期範圍是否過大（超過1年會警告）
    if (filters.startDate && filters.endDate) {
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      const daysDiff = (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24);
      
      if (daysDiff > 365) {
        const proceed = window.confirm(
          language === 'zh' 
            ? '日期範圍超過1年，查詢可能較慢。是否繼續？' 
            : 'Date range exceeds 1 year, query may be slow. Continue?'
        );
        if (!proceed) return;
      }
    }

    // 🔄 使用專業的刷新搜索機制
    console.log('🔄 觸發專業刷新搜索，條件:', filters);
    triggerReloadSearch(filters);
  };

  // 開始編輯
  const startEdit = (order: SalesOrder) => {
    setEditingId(order.id);
    setEditValues({
      sale_commission_rate: order.sale_commission_rate,
      snapshot_cost_unit: order.snapshot_cost_unit
    });
  };

  // 取消編輯
  const cancelEdit = () => {
    setEditingId(null);
    setEditValues({
      sale_commission_rate: null,
      snapshot_cost_unit: null
    });
  };

  // 儲存修改並重新計算
  const saveEdit = async (orderId: number) => {
    // 檢查是否有公司資訊
    if (!currentCompany?.id) {
      toast.error(language === 'zh' ? '缺少公司資訊，無法更新' : 'Missing company information, cannot update');
      return;
    }

    // 創建新的 AbortController 用於這個操作
    const controller = new AbortController();
    setLoading(true);
    try {
      // 先取得當前記錄的資料以計算佣金金額
      const currentOrder = salesData.find(order => order.id === orderId);
      if (!currentOrder) {
        toast.error(language === 'zh' ? '找不到訂單記錄' : 'Order record not found');
        return;
      }

      // 計算佣金金額
      const commissionRate = editValues.sale_commission_rate || 0;
      const totalAmount = currentOrder.total_amount || 0;
      const salesCommissionAmount = (totalAmount * commissionRate) / 100;

      // 更新銷售訂單，包含手動計算的佣金金額
      const { error: updateError } = await supabase
        .from('sales_orders')
        .update({
          sale_commission_rate: editValues.sale_commission_rate,
          snapshot_cost_unit: editValues.snapshot_cost_unit,
          sales_commission_amount: salesCommissionAmount
        })
        .eq('id', orderId)
        .eq('company_id', Number(currentCompany?.id))
        .abortSignal(controller.signal);

      if (updateError) {
        console.error('更新錯誤:', updateError);
        toast.error(language === 'zh' ? `更新失敗: ${updateError.message}` : `Update failed: ${updateError.message}`);
        return;
      }

      // 重新查詢更新後的資料以獲取重新計算的欄位
      await searchSalesOrders();
      
      setEditingId(null);
      setEditValues({
        sale_commission_rate: null,
        snapshot_cost_unit: null
      });
      
      toast.success(language === 'zh' ? '更新成功，相關計算已重新處理' : 'Updated successfully, calculations refreshed');
    } catch (error) {
      console.error('儲存錯誤:', error);
      toast.error(language === 'zh' ? '儲存過程中發生錯誤' : 'Error occurred during save');
    } finally {
      setLoading(false);
    }
  };

  const buildXlsxAndDownload = async (rows: SalesOrder[], filenamePrefix: string) => {
    // 動態載入避免首頁 bundle 變大
    const XLSX = await import('xlsx-js-style');

    // Excel 欄位（含新增欄位）
    const headers = [
      'Order Date',
      'Product Name',
      'Brand',
      'SKU ID',
      'Sub-Order ID',
      'Shipment ID',
      'Sale Quantity',
      'Net Revenue',
      'Gross Profit',
      'Total Amount'
    ];

    const aoa: (string | number)[][] = [
      headers,
      ...rows.map(order => [
        order.order_date ? `${new Date(order.order_date).toLocaleDateString()}${order.order_time ? ` ${order.order_time}` : ''}` : '',
        order.product_name || '',
        order.brand || '',
        // SKU ID：強制文字，避免 Excel 科學記號/去掉前導 0
        String(order.sku_id ?? ''),
        // Sub-Order ID：此頁使用 sales_orders.order_id 作為 Sub-Order ID
        String(order.order_id ?? ''),
        String(order.shipment_id ?? ''),
        Number(order.sale_quantity ?? 0),
        order.snapshot_net_revenue ?? '',
        order.snapshot_gross_profit ?? '',
        order.total_amount ?? ''
      ])
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // 讓 SKU / Sub-Order / Shipment 欄位固定為文字（t='s'、格式 '@'）
    const setTextCol = (colIndex0: number) => {
      for (let r = 1; r < aoa.length; r++) {
        const cellAddr = XLSX.utils.encode_cell({ r, c: colIndex0 });
        const cell = ws[cellAddr];
        if (!cell) continue;
        cell.t = 's';
        (cell as any).z = '@';
        cell.v = String(cell.v ?? '');
      }
    };
    setTextCol(3); // SKU ID
    setTextCol(4); // Sub-Order ID
    setTextCol(5); // Shipment ID

    // 基本欄寬
    ws['!cols'] = [
      { wch: 18 }, // Order Date
      { wch: 32 }, // Product Name
      { wch: 18 }, // Brand
      { wch: 18 }, // SKU ID
      { wch: 20 }, // Sub-Order ID
      { wch: 20 }, // Shipment ID
      { wch: 12 }, // Qty
      { wch: 14 }, // Net
      { wch: 14 }, // GP
      { wch: 14 }  // Total
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sales');

    const filename = `${filenamePrefix}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const fetchAllSalesDataForExport = async (filters: SearchFilters, companyId: number) => {
    const pageSize = 1000; // PostgREST 常見上限；用 range 分批抓全量資料
    let from = 0;
    let all: SalesOrder[] = [];

    while (true) {
      let query = supabase
        .from('sales_orders')
        .select(`
          id,
          order_date,
          order_time,
          order_id,
          sku_id,
          company_sku_key,
          sale_quantity,
          sale_unit_price,
          sale_commission_rate,
          sales_commission_amount,
          snapshot_cost_unit,
          snapshot_net_revenue,
          snapshot_gross_profit,
          total_amount,
          shipment_id,
          sale_sku_brand_en,
          sale_sku_brand_zh,
          sale_sub_category_code,
          sale_sku_name_en,
          sale_sku_name_zh
        `)
        .eq('company_id', companyId)
        .range(from, from + pageSize - 1)
        .order('id', { ascending: true });

      // 套用同一套篩選條件
      if (filters.startDate) query = query.gte('order_date', filters.startDate);
      if (filters.endDate) query = query.lte('order_date', filters.endDate);
      if (filters.orderId) query = query.ilike('order_id', `%${filters.orderId}%`);
      if (filters.skuId) query = query.or(`sku_id.ilike.%${filters.skuId}%,company_sku_key.ilike.%${filters.skuId}%`);
      if (filters.shipmentId) query = query.ilike('shipment_id', `%${filters.shipmentId}%`);
      if (filters.brand) query = query.or(`sale_sku_brand_en.ilike.%${filters.brand}%,sale_sku_brand_zh.ilike.%${filters.brand}%`);
      if (filters.categoryCode) query = query.ilike('sale_sub_category_code', `%${filters.categoryCode}%`);
      if (filters.productName) query = query.or(`sale_sku_name_en.ilike.%${filters.productName}%,sale_sku_name_zh.ilike.%${filters.productName}%`);

      const { data, error } = await query;
      if (error) throw error;

      const page = (data ?? []).map(item => ({
        ...item,
        id: Number(item.id),
        sale_quantity: toNum(item.sale_quantity) ?? 0,
        sale_unit_price: toNum(item.sale_unit_price) ?? 0,
        sale_commission_rate: toNum(item.sale_commission_rate),
        sales_commission_amount: toNum(item.sales_commission_amount),
        snapshot_cost_unit: toNum(item.snapshot_cost_unit),
        snapshot_net_revenue: toNum(item.snapshot_net_revenue),
        snapshot_gross_profit: toNum(item.snapshot_gross_profit),
        total_amount: toNum(item.total_amount),
        product_name: item.sale_sku_name_zh || item.sale_sku_name_en || `SKU: ${item.sku_id}`,
        brand: item.sale_sku_brand_zh || item.sale_sku_brand_en || '-'
      })) as SalesOrder[];

      all = all.concat(page);
      if (page.length < pageSize) break;

      from += pageSize;
    }

    return all;
  };

  // 匯出功能（全量資料，不受畫面顯示筆數限制）
  const exportData = async () => {
    if (!currentCompany?.id) {
      toast.error(language === 'zh' ? '缺少公司資訊，無法匯出' : 'Missing company information, cannot export');
      return;
    }

    setLoading(true);
    try {
      await ensureAuthReady();
      const companyId = Number(currentCompany.id);
      const allRows = await fetchAllSalesDataForExport(filters, companyId);

      if (allRows.length === 0) {
        toast.error(language === 'zh' ? '沒有資料可匯出' : 'No data to export');
        return;
      }

      await buildXlsxAndDownload(allRows, 'sales_export');
      toast.success(language === 'zh' ? `匯出成功（${allRows.length} 筆）` : `Export successful (${allRows.length} rows)`);
    } catch (e: any) {
      console.error('匯出失敗:', e);
      toast.error(language === 'zh' ? '匯出失敗，請稍後再試' : 'Export failed, please try again later');
    } finally {
      setLoading(false);
    }
  };

  // 清除所有篩選條件
  const clearFilters = () => {
    const yesterday = getYesterday();
    setFilters({
      startDate: yesterday,
      endDate: yesterday,
      orderId: '',
      skuId: '',
      shipmentId: '',
      brand: '',
      categoryCode: '',
      productName: ''
    });
    // 清除搜尋結果和排序狀態
    setSalesData([]);
    setSortConfig({ key: null, direction: null });
    toast.info(language === 'zh' ? '篩選條件已清除，日期重設為昨天' : 'Filters cleared, dates reset to yesterday');
  };

  // 組件卸載時清理
  useEffect(() => {
    return () => {
      ctrlRef.current?.abort();
    };
  }, []);

  // 🧭 看門狗：避免 UI 永久卡 loading
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => {
      console.warn('🧭[WATCHDOG] force clear loading after 31s');
      setLoading(false);
    }, 31_000);
    return () => clearTimeout(t);
  }, [loading]);

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => {
              localStorage.setItem('selectedCategory', ORDER_CATEGORY);
              navigate(ROUTES.dashboard.root);
            }}
            className="bg-card/50 border-border text-muted-foreground hover:bg-accent/50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'zh' ? '返回主控台' : 'Back to Dashboard'}
          </Button>
          <div className="flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-purple-400" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">{language === 'zh' ? '銷售統計' : 'Sales Statistics'}</h1>
          </div>
        </div>

        {/* 🧪 測試探針 - 在開發時顯示 */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mb-6">
            <TestSearchHarness />
          </div>
        )}

        {/* 搜尋條件區域 */}
        <Card className="bg-card/80 border-border mb-6">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <Search className="w-5 h-5" />
              {language === 'zh' ? '搜尋條件' : 'Search Criteria'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <Label htmlFor="startDate" className="text-muted-foreground">{language === 'zh' ? '開始日期' : 'Start Date'}</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  className="bg-background/50 border-input text-foreground"
                />
              </div>
              <div>
                <Label htmlFor="endDate" className="text-muted-foreground">{language === 'zh' ? '結束日期' : 'End Date'}</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  className="bg-background/50 border-input text-foreground"
                />
              </div>
              <div>
                <Label htmlFor="orderId" className="text-muted-foreground">Order ID</Label>
                <Input
                  id="orderId"
                  value={filters.orderId}
                  onChange={(e) => setFilters(prev => ({ ...prev, orderId: e.target.value }))}
                  className="bg-background/50 border-input text-foreground"
                  placeholder={language === 'zh' ? '輸入訂單編號' : 'Enter Order ID'}
                />
              </div>
              <div>
                <Label htmlFor="skuId" className="text-muted-foreground">SKU ID</Label>
                <Input
                  id="skuId"
                  value={filters.skuId}
                  onChange={(e) => setFilters(prev => ({ ...prev, skuId: e.target.value }))}
                  className="bg-background/50 border-input text-foreground"
                  placeholder={language === 'zh' ? '輸入產品編號' : 'Enter SKU ID'}
                />
              </div>
              <div>
                <Label htmlFor="shipmentId" className="text-muted-foreground">Shipment ID</Label>
                <Input
                  id="shipmentId"
                  value={filters.shipmentId}
                  onChange={(e) => setFilters(prev => ({ ...prev, shipmentId: e.target.value }))}
                  className="bg-background/50 border-input text-foreground"
                  placeholder={language === 'zh' ? '輸入貨運編號' : 'Enter Shipment ID'}
                />
              </div>
              <div>
                <Label htmlFor="brand" className="text-muted-foreground">{language === 'zh' ? '品牌' : 'Brand'}</Label>
                <Input
                  id="brand"
                  value={filters.brand}
                  onChange={(e) => setFilters(prev => ({ ...prev, brand: e.target.value }))}
                  className="bg-background/50 border-input text-foreground"
                  placeholder={language === 'zh' ? '輸入中文或英文品牌名' : 'Enter brand name (Chinese or English)'}
                />
              </div>
              <div>
                <Label htmlFor="categoryCode" className="text-muted-foreground">{language === 'zh' ? '分類碼' : 'Category Code'}</Label>
                <Input
                  id="categoryCode"
                  value={filters.categoryCode}
                  onChange={(e) => setFilters(prev => ({ ...prev, categoryCode: e.target.value }))}
                  className="bg-background/50 border-input text-foreground"
                  placeholder={language === 'zh' ? '輸入分類碼' : 'Enter Category Code'}
                />
              </div>
              <div>
                <Label htmlFor="productName" className="text-muted-foreground">{language === 'zh' ? '產品名稱' : 'Product Name'}</Label>
                <Input
                  id="productName"
                  value={filters.productName}
                  onChange={(e) => setFilters(prev => ({ ...prev, productName: e.target.value }))}
                  className="bg-background/50 border-input text-foreground"
                  placeholder={language === 'zh' ? '輸入中文或英文產品名稱' : 'Enter product name (Chinese or English)'}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={searchSalesOrders} 
                disabled={loading}
                className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white"
              >
                <Search className="w-4 h-4 mr-2" />
                {loading ? (language === 'zh' ? '搜尋中...' : 'Searching...') : (language === 'zh' ? '搜尋' : 'Search')}
              </Button>
              <Button 
                onClick={clearFilters} 
                disabled={loading}
                variant="outline"
                className="bg-card/50 border-border text-foreground hover:bg-secondary/50"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {language === 'zh' ? '清除篩選' : 'Clear Filters'}
              </Button>
              <Button 
                onClick={exportData} 
                disabled={salesData.length === 0}
                variant="outline"
                className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50"
              >
                <Download className="w-4 h-4 mr-2" />
                {language === 'zh' ? '匯出' : 'Export'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* 結果顯示區域 */}
        {salesData.length > 0 && (
          <Card className="bg-card/80 border-border">
            <CardHeader>
              <CardTitle className="text-foreground">
                {language === 'zh' ? `搜尋結果 (${salesData.length} 筆記錄)` : `Search Results (${salesData.length} records)`}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table className="w-full">
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead
                        className="text-muted-foreground cursor-pointer hover:bg-accent/50 select-none"
                        onClick={() => handleSort('order_date')}
                      >
                        <div className="flex items-center gap-1">
                          {language === 'zh' ? '訂單日期' : 'Order Date'}
                          {sortConfig.key === 'order_date' && (
                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 text-purple-400" /> :
                            sortConfig.direction === 'desc' ? <ChevronDown className="w-4 h-4 text-purple-400" /> : null
                          )}
                        </div>
                      </TableHead>
                      <TableHead
                        className="text-muted-foreground cursor-pointer hover:bg-accent/50 select-none"
                        onClick={() => handleSort('sku_id')}
                      >
                        <div className="flex items-center gap-1">
                          SKU ID
                          {sortConfig.key === 'sku_id' && (
                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 text-purple-400" /> :
                            sortConfig.direction === 'desc' ? <ChevronDown className="w-4 h-4 text-purple-400" /> : null
                          )}
                        </div>
                      </TableHead>
                      <TableHead
                        className="text-muted-foreground cursor-pointer hover:bg-accent/50 select-none"
                        onClick={() => handleSort('product_name')}
                      >
                        <div className="flex items-center gap-1">
                          {language === 'zh' ? '產品名稱' : 'Product Name'}
                          {sortConfig.key === 'product_name' && (
                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 text-purple-400" /> :
                            sortConfig.direction === 'desc' ? <ChevronDown className="w-4 h-4 text-purple-400" /> : null
                          )}
                        </div>
                      </TableHead>
                      <TableHead
                        className="text-muted-foreground cursor-pointer hover:bg-accent/50 select-none"
                        onClick={() => handleSort('brand')}
                      >
                        <div className="flex items-center gap-1">
                          {language === 'zh' ? '品牌' : 'Brand'}
                          {sortConfig.key === 'brand' && (
                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 text-purple-400" /> :
                            sortConfig.direction === 'desc' ? <ChevronDown className="w-4 h-4 text-purple-400" /> : null
                          )}
                        </div>
                      </TableHead>
                      <TableHead
                        className="text-muted-foreground cursor-pointer hover:bg-accent/50 select-none"
                        onClick={() => handleSort('sale_sub_category_code')}
                      >
                        <div className="flex items-center gap-1">
                          {language === 'zh' ? '分類碼' : 'Category Code'}
                          {sortConfig.key === 'sale_sub_category_code' && (
                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 text-purple-400" /> :
                            sortConfig.direction === 'desc' ? <ChevronDown className="w-4 h-4 text-purple-400" /> : null
                          )}
                        </div>
                      </TableHead>
                      <TableHead
                        className="text-muted-foreground cursor-pointer hover:bg-accent/50 select-none"
                        onClick={() => handleSort('sale_quantity')}
                      >
                        <div className="flex items-center gap-1">
                          {language === 'zh' ? '銷售數量' : 'Sale Quantity'}
                          {sortConfig.key === 'sale_quantity' && (
                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 text-purple-400" /> :
                            sortConfig.direction === 'desc' ? <ChevronDown className="w-4 h-4 text-purple-400" /> : null
                          )}
                        </div>
                      </TableHead>
                      <TableHead
                        className="text-muted-foreground cursor-pointer hover:bg-accent/50 select-none"
                        onClick={() => handleSort('snapshot_net_revenue')}
                      >
                        <div className="flex items-center gap-1">
                          {language === 'zh' ? '淨收入' : 'Net Revenue'}
                          {sortConfig.key === 'snapshot_net_revenue' && (
                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 text-purple-400" /> :
                            sortConfig.direction === 'desc' ? <ChevronDown className="w-4 h-4 text-purple-400" /> : null
                          )}
                        </div>
                      </TableHead>
                      <TableHead
                        className="text-muted-foreground cursor-pointer hover:bg-accent/50 select-none"
                        onClick={() => handleSort('sale_commission_rate')}
                      >
                        <div className="flex items-center gap-1">
                          {language === 'zh' ? '佣金率' : 'Commission Rate'}
                          {sortConfig.key === 'sale_commission_rate' && (
                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 text-purple-400" /> :
                            sortConfig.direction === 'desc' ? <ChevronDown className="w-4 h-4 text-purple-400" /> : null
                          )}
                        </div>
                      </TableHead>
                      <TableHead
                        className="text-muted-foreground cursor-pointer hover:bg-accent/50 select-none"
                        onClick={() => handleSort('snapshot_cost_unit')}
                      >
                        <div className="flex items-center gap-1">
                          {language === 'zh' ? '單一成本' : 'Unit Cost'}
                          {sortConfig.key === 'snapshot_cost_unit' && (
                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 text-purple-400" /> :
                            sortConfig.direction === 'desc' ? <ChevronDown className="w-4 h-4 text-purple-400" /> : null
                          )}
                        </div>
                      </TableHead>
                      <TableHead
                        className="text-muted-foreground cursor-pointer hover:bg-accent/50 select-none"
                        onClick={() => handleSort('snapshot_gross_profit')}
                      >
                        <div className="flex items-center gap-1">
                          {language === 'zh' ? '毛利' : 'Gross Profit'}
                          {sortConfig.key === 'snapshot_gross_profit' && (
                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 text-purple-400" /> :
                            sortConfig.direction === 'desc' ? <ChevronDown className="w-4 h-4 text-purple-400" /> : null
                          )}
                        </div>
                      </TableHead>
                      <TableHead
                        className="text-muted-foreground cursor-pointer hover:bg-accent/50 select-none"
                        onClick={() => handleSort('total_amount')}
                      >
                        <div className="flex items-center gap-1">
                          {language === 'zh' ? '單據總金額' : 'Total Amount'}
                          {sortConfig.key === 'total_amount' && (
                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 text-purple-400" /> :
                            sortConfig.direction === 'desc' ? <ChevronDown className="w-4 h-4 text-purple-400" /> : null
                          )}
                        </div>
                      </TableHead>
                      <TableHead className="text-slate-300">{language === 'zh' ? '操作' : 'Actions'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedSalesData.map((order) => (
                      <TableRow key={order.id} className="border-border hover:bg-accent/50">
                        <TableCell className="text-muted-foreground">
                          {order.order_date ? (
                            <div>
                              <div>{new Date(order.order_date).toLocaleDateString()}</div>
                              {order.order_time && (
                                <div className="text-xs text-muted-foreground/70">{order.order_time}</div>
                              )}
                            </div>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          <div>{order.sku_id || '-'}</div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{decodeHtmlEntities(order.product_name || '-')}</TableCell>
                        <TableCell className="text-muted-foreground">{order.brand || '-'}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {order.sale_sub_category_code ? (
                            <div className="max-w-[120px]">
                              {order.sale_sub_category_code.split(',').slice(0, 3).map((code, index) => (
                                <div key={index} className="text-xs leading-tight">
                                  {code.trim()}
                                </div>
                              ))}
                            </div>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{order.sale_quantity}</TableCell>
                        <TableCell className="text-muted-foreground">${order.snapshot_net_revenue?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {editingId === order.id ? (
                            <Input
                              type="number"
                              step="0.01"
                              value={editValues.sale_commission_rate || ''}
                              onChange={(e) => setEditValues(prev => ({ 
                                ...prev, 
                                sale_commission_rate: parseFloat(e.target.value) || null 
                              }))}
                              className="w-20 bg-background/50 border-input text-foreground"
                            />
                          ) : (
                            `${order.sale_commission_rate?.toFixed(2) || 0}%`
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {editingId === order.id ? (
                            <Input
                              type="number"
                              step="0.01"
                              value={editValues.snapshot_cost_unit || ''}
                              onChange={(e) => setEditValues(prev => ({ 
                                ...prev, 
                                snapshot_cost_unit: parseFloat(e.target.value) || null 
                              }))}
                              className="w-20 bg-background/50 border-input text-foreground"
                            />
                          ) : (
                            `$${order.snapshot_cost_unit?.toFixed(2) || '0.00'}`
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">${order.snapshot_gross_profit?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell className="text-muted-foreground">${order.total_amount?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell>
                          {editingId === order.id ? (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                onClick={() => saveEdit(order.id)}
                                disabled={loading}
                                className="bg-green-600 hover:bg-green-700 text-white"
                              >
                                <Check className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={cancelEdit}
                                className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => startEdit(order)}
                              className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50"
                            >
                              <Edit3 className="w-3 h-3" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 無資料顯示 */}
        {!loading && salesData.length === 0 && (
          <Card className="bg-card/80 border-border">
            <CardContent className="text-center py-8">
              <p className="text-muted-foreground">
                {language === 'zh' ? '請使用上方搜尋條件查找銷售資料' : 'Please use the search criteria above to find sales data'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SalesStatistics;


